/**
 * Rex Mortis — Big-number formatting.
 *
 * Canonical formatters for stat values, crowns, and any other number that can
 * grow astronomically in the late game (stats compound past 1T into trillions
 * and beyond under the inverted training curve).
 *
 * Suffix ladder (game-UI standard, matches Cookie-Clicker / AdVenture-Capitalist):
 *   K   thousand        10^3
 *   M   million         10^6
 *   B   billion         10^9
 *   T   trillion        10^12
 *   Qa  quadrillion     10^15
 *   Qi  quintillion     10^18
 *   Sx  sextillion      10^21
 *   Sp  septillion      10^24
 *   Oc  octillion       10^27
 *   No  nonillion       10^30
 *   Dc  decillion       10^33
 *   →   scientific notation past 10^36 ("1.2e36")
 *
 * Two entry points:
 *   - formatBigNum(n: number)  — for in-memory numbers
 *   - formatBigStr(s: string)  — for BigInt-safe string-encoded numbers (e.g.
 *                                 crowns received as strings from the API)
 *
 * Both produce the same suffix vocabulary so the UI is consistent.
 */

interface Suffix { v: number; s: string }

/**
 * Ordered largest → smallest so the first match wins. Past `Dc` we fall back
 * to scientific notation rather than invent obscure suffixes.
 */
const SUFFIXES: readonly Suffix[] = [
  { v: 1e33, s: 'Dc' },
  { v: 1e30, s: 'No' },
  { v: 1e27, s: 'Oc' },
  { v: 1e24, s: 'Sp' },
  { v: 1e21, s: 'Sx' },
  { v: 1e18, s: 'Qi' },
  { v: 1e15, s: 'Qa' },
  { v: 1e12, s: 'T'  },
  { v: 1e9,  s: 'B'  },
  { v: 1e6,  s: 'M'  },
  { v: 1e3,  s: 'K'  },
];

/** Threshold past which we give up on suffixes and use scientific notation. */
const SCIENTIFIC_THRESHOLD = 1e36;

/** Trim trailing zeros from a fixed-decimal output ("1.20" → "1.2", "1.00" → "1"). */
function trimFraction(s: string): string {
  if (!s.includes('.')) return s;
  return s.replace(/0+$/, '').replace(/\.$/, '');
}

/**
 * Format a numeric value with K/M/B/T/Qa/.../Dc suffixes. Values smaller than
 * 1,000 are returned unchanged (no suffix). Negative values are formatted as
 * the positive value with a leading "-".
 */
export function formatBigNum(n: number): string {
  if (!Number.isFinite(n)) return String(n);
  if (n === 0) return '0';
  const negative = n < 0;
  const abs = Math.abs(n);

  if (abs >= SCIENTIFIC_THRESHOLD) {
    return (negative ? '-' : '') + abs.toExponential(2).replace('+', '');
  }
  if (abs < 1_000) {
    // Show small numbers as-is; preserve a couple of decimals so display
    // doesn't lie about precision.
    return (negative ? '-' : '') + (Number.isInteger(abs) ? abs.toString() : abs.toFixed(2).replace(/\.?0+$/, ''));
  }

  for (const u of SUFFIXES) {
    if (abs >= u.v) {
      const x = abs / u.v;
      const fixed = x >= 100 ? x.toFixed(0) : x >= 10 ? x.toFixed(1) : x.toFixed(2);
      return (negative ? '-' : '') + trimFraction(fixed) + u.s;
    }
  }
  return (negative ? '-' : '') + abs.toString();
}

/**
 * Format a string-encoded number (BigInt-safe). Parses the leading whole-part
 * length to bucket into a suffix without losing precision for very large
 * crown totals. Falls back to formatBigNum for anything safely representable
 * as a JS number.
 */
export function formatBigStr(s: string): string {
  if (!s) return '0';
  const negative = s.startsWith('-');
  const raw = negative ? s.slice(1) : s;

  // Strip any non-digit prefix or stray content — we expect plain digits.
  if (!/^\d+$/.test(raw)) {
    const asNum = Number(s);
    if (Number.isFinite(asNum)) return formatBigNum(asNum);
    return s;
  }

  const len = raw.length;

  // Under 1,000 — show full number.
  if (len < 4) return (negative ? '-' : '') + raw;

  // Past the safe-integer range, manually pick a suffix by digit count so we
  // don't lose precision converting to a JS number.
  const tiers: Array<{ minDigits: number; suffix: string }> = [
    { minDigits: 34, suffix: 'Dc' },
    { minDigits: 31, suffix: 'No' },
    { minDigits: 28, suffix: 'Oc' },
    { minDigits: 25, suffix: 'Sp' },
    { minDigits: 22, suffix: 'Sx' },
    { minDigits: 19, suffix: 'Qi' },
    { minDigits: 16, suffix: 'Qa' },
    { minDigits: 13, suffix: 'T'  },
    { minDigits: 10, suffix: 'B'  },
    { minDigits: 7,  suffix: 'M'  },
    { minDigits: 4,  suffix: 'K'  },
  ];

  // Scientific notation past decillion + 3 digits (1e36).
  if (len > 36) {
    const mantissa = `${raw[0]}.${raw.slice(1, 3)}`;
    const exp = len - 1;
    return (negative ? '-' : '') + mantissa + 'e' + exp;
  }

  for (const { minDigits, suffix } of tiers) {
    if (len >= minDigits) {
      const wholeLen = len - (minDigits - 1);
      const whole = raw.slice(0, wholeLen);
      const frac = raw.slice(wholeLen, wholeLen + 2);
      // Strip trailing zeros from the fraction so "1.00M" → "1M".
      const fracTrim = frac.replace(/0+$/, '');
      return (negative ? '-' : '') + whole + (fracTrim ? '.' + fracTrim : '') + suffix;
    }
  }

  return (negative ? '-' : '') + raw;
}

// ──────────────────────────────────────────────────────────────────────────────
// CROWN FORMATTING
//
// The canonical short-form currency display, used for EVERY crown amount the
// game shows — rewards, costs, wages, balances, treasuries. Suffixes are
// LOWERCASE so crowns read distinctly from battle/working stats (which use
// uppercase K/M/... via formatBigNum). One decimal place, trailing zeros
// stripped:
//   850            → "850"
//   12_000         → "12k"
//   12_500         → "12.5k"
//   1_500_000      → "1.5m"
//   2_300_000_000  → "2.3b"
//
// formatCrowns()      — short from 1,000 up. Use everywhere on every page.
// formatCrownsFull()  — full comma-separated number below 1,000,000, short
//                       form at 1m and above. Reserved for the player's
//                       running crown total in the sidebar, where precision
//                       at small balances matters.
//
// Both accept a number, bigint, or BigInt-safe string.
// ──────────────────────────────────────────────────────────────────────────────

const CROWN_SUFFIX: ReadonlyArray<{ minDigits: number; suffix: string }> = [
  { minDigits: 34, suffix: 'dc' },
  { minDigits: 31, suffix: 'no' },
  { minDigits: 28, suffix: 'oc' },
  { minDigits: 25, suffix: 'sp' },
  { minDigits: 22, suffix: 'sx' },
  { minDigits: 19, suffix: 'qi' },
  { minDigits: 16, suffix: 'qa' },
  { minDigits: 13, suffix: 't'  },
  { minDigits: 10, suffix: 'b'  },
  { minDigits: 7,  suffix: 'm'  },
  { minDigits: 4,  suffix: 'k'  },
];

/** Normalise any crown input into { negative, digit-string }. */
function crownDigits(value: string | number | bigint): { neg: boolean; raw: string } {
  let s: string;
  if (typeof value === 'number') s = Math.round(value).toString();
  else if (typeof value === 'bigint') s = value.toString();
  else s = value;
  const neg = s.startsWith('-');
  let raw = neg ? s.slice(1) : s;
  // Defensively strip anything past the leading run of digits.
  raw = raw.replace(/[^\d].*$/, '');
  if (raw === '') raw = '0';
  return { neg, raw };
}

/** Digit-string → short form with a lowercase suffix. Scientific past 1e36. */
function crownShortFromDigits(raw: string): string {
  const len = raw.length;
  if (len > 36) {
    const mantissa = `${raw[0]}.${raw.slice(1, 3)}`;
    return mantissa + 'e' + (len - 1);
  }
  for (const { minDigits, suffix } of CROWN_SUFFIX) {
    if (len >= minDigits) {
      const wholeLen = len - (minDigits - 1);
      const whole = raw.slice(0, wholeLen);
      const frac = raw.slice(wholeLen, wholeLen + 1); // one decimal place
      const fracTrim = frac.replace(/0+$/, '');
      return whole + (fracTrim ? '.' + fracTrim : '') + suffix;
    }
  }
  return raw; // under 1,000 — shown as-is
}

/** Short-form crown display (12000 → "12k"). The default for every page. */
export function formatCrowns(value: string | number | bigint): string {
  const { neg, raw } = crownDigits(value);
  return (neg ? '-' : '') + crownShortFromDigits(raw);
}

/**
 * Header/sidebar crown total: full comma number below 1,000,000, short form
 * at 1m and above. Keeps small balances precise where the running total lives.
 */
export function formatCrownsFull(value: string | number | bigint): string {
  const { neg, raw } = crownDigits(value);
  if (raw.length < 7) {
    const parts: string[] = [];
    for (let i = raw.length; i > 0; i -= 3) {
      parts.unshift(raw.slice(Math.max(0, i - 3), i));
    }
    return (neg ? '-' : '') + parts.join(',');
  }
  return (neg ? '-' : '') + crownShortFromDigits(raw);
}
